import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { resolve } from 'styled-jsx/css';
import AutolistButtonStyles from 'components/styles/AutolistButton';
import emptyReviewsImage from 'public/static/images/dealer_pages/empty-review-state.svg';
import { screen, colors } from 'components/styles/variables';
import { trackDealerPagesClick } from 'lib/Tracking/DealerPages';
import { DealerPagesEventType } from 'lib/EventSchema';
import TrackingDataContext from 'contexts/TrackingDataContext';
import { DealerReview } from 'lib/models/DealerReview';
import Review from './Review';

const INITIAL_REVIEWS_SHOWN = 3;

/* istanbul ignore next */
const dealerButtonStyles = resolve`
  @media (min-width: ${screen.smMin}) {
    .dealer-button {
      max-width: 310px;
    }
  }
`;

interface ReviewsListProps {
  reviews: DealerReview[];
}

function ReviewsList({ reviews = [] }: ReviewsListProps): JSX.Element {
  const { t } = useTranslation();
  const { dealerPageUrl } = useContext(TrackingDataContext);

  const [isExpanded, setIsExpanded] = useState(INITIAL_REVIEWS_SHOWN >= reviews.length);

  function onClick(): void {
    setIsExpanded(true);
    trackDealerPagesClick({
      type: DealerPagesEventType.show_more_reviews_click,
      dealerPageUrl,
    });
  }

  const reviewsLimit = isExpanded ? reviews.length : INITIAL_REVIEWS_SHOWN;

  return (
    <div>
      {reviews.slice(0, reviewsLimit).map(({ id, ...rest }) => (
        <Review key={id} {...rest} />
      ))}

      {!isExpanded && (
        <>
          <div className="show-more">
            <button
              data-testid="show-more"
              className={`dealer-button autolist-button ${AutolistButtonStyles.className} ${dealerButtonStyles.className}`}
              onClick={onClick}
            >
              {t('dealerPages.showMoreReviews')}
            </button>
          </div>
          <div className="count">
            {t('dealerPages.reviewsDisplayed', {
              reviewsShownCount: Math.min(reviews.length, INITIAL_REVIEWS_SHOWN),
              totalReviewsCount: reviews.length,
            })}{' '}
          </div>
        </>
      )}
      {AutolistButtonStyles.styles}
      {dealerButtonStyles.styles}
      <style jsx>{`
        .show-more {
          display: flex;
          justify-content: center;
        }

        .count {
          margin-top: 10px;
          margin-bottom: 10px;
          text-align: center;
          font-size: 14px;
          font-weight: 400;
          color: ${colors.G3};
        }
      `}</style>
    </div>
  );
}

function EmptyReviews(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="empty-container" data-testid="empty">
      <img className="empty-reviews-image" src={emptyReviewsImage} alt="No reviews" />
      <div className="empty-label">{t('dealerPages.beTheFirstToShareYourExperience')}</div>
      <div className="write-review">
        <a
          href="https://www.research.net/r/autolist-dealer-review"
          target="_blank"
          aria-label={t('accessibility.newWindow')}
          className={`dealer-button autolist-button ${dealerButtonStyles.className} ${AutolistButtonStyles.className}`}
        >
          {t('dealerPages.writeAReview')}
        </a>
      </div>
      {dealerButtonStyles.styles}
      {AutolistButtonStyles.styles}
      <style jsx>{`
        .empty-container {
          width: 100%;
          text-align: center;
        }

        .empty-label {
          font-size: 16px;
          font-weight: 900;
          text-align: center;
        }

        .empty-reviews-image {
          display: inline-block;
        }

        .write-review {
          margin-top: 17px;
          display: flex;
          justify-content: center;
        }

        .autolist-button {
          display: block;
        }
      `}</style>
    </div>
  );
}

interface ReviewsProps {
  reviews: DealerReview[];
}

export default function Reviews({ reviews = [] }: ReviewsProps): JSX.Element {
  return (
    <div className="reviews-container">
      {reviews.length > 0 ? <ReviewsList reviews={reviews} /> : <EmptyReviews />}
      <style jsx>{`
        .reviews-container {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          padding: 25px 15px 50px 15px;
        }
      `}</style>
    </div>
  );
}
